import { getMessageHash } from './get-message-hash';

export const MAINTENANCE_DISMISSAL_HOURS = 24;
export const MAINTENANCE_DISMISSAL_COOKIE_PREFIX = 'maintenance-banner-dismissed-';

export type MaintenanceBannerState = {
  /** Whether the banner should be rendered. */
  isVisible: boolean;
  /**
   * Cookie key used to record dismissal. Empty string when there is no
   * message (banner cannot be dismissed if it isn't shown).
   */
  dismissalCookieKey: string;
  /** Trimmed message text — what the banner should display. */
  message: string;
};

/**
 * Computes the maintenance banner's render state from the env var value
 * and the user's existing dismissal cookie. Pure, deterministic, and
 * trivially unit-testable.
 *
 * Hides the banner when:
 *   - the message is empty / whitespace-only, or
 *   - the user has previously dismissed this exact message text
 *     (the dismissal cookie is keyed by a hash of the message, so a
 *     new message automatically re-shows for users who dismissed an
 *     earlier one).
 */
export function getMaintenanceBannerState(
  rawMessage: string | undefined,
  getCookie: (key: string) => string | undefined
): MaintenanceBannerState {
  const message = (rawMessage ?? '').trim();

  if (!message) {
    return { isVisible: false, dismissalCookieKey: '', message: '' };
  }

  const dismissalCookieKey = `${MAINTENANCE_DISMISSAL_COOKIE_PREFIX}${getMessageHash(message)}`;
  const isVisible = getCookie(dismissalCookieKey) !== 'true';

  return { isVisible, dismissalCookieKey, message };
}
